import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Settings, 
  Warning, 
  Refresh, 
  ExpandMore, 
  Science, 
  CheckCircle, 
  Error as ErrorIcon,
  Storage 
} from '@mui/icons-material';
import { adSettingsService, AdSettings } from '../../services/AdSettingsService';
import { reviveAdService } from '../../services/ReviveAdService';

/**
 * Ad Settings Component
 * Allows users to configure ad system preferences
 */
const AdSettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<AdSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState<{ key: keyof AdSettings; value: boolean } | null>(null);
  const [reviveServerUrl, setReviveServerUrl] = useState('');
  const [reviveZones, setReviveZones] = useState({ header: 1, sidebar: 2, mobile: 3, inContent: 4, footer: 5 });
  const [testingRevive, setTestingRevive] = useState(false);
  const [reviveTestResult, setReviveTestResult] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const currentSettings = await adSettingsService.getSettings();
        setSettings(currentSettings);
        setReviveServerUrl(currentSettings.reviveServerUrl);
        setReviveZones(currentSettings.reviveZones);
      } catch (error) {
        console.error('Error loading ad settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSettingChange = async (key: keyof AdSettings, value: boolean) => {
    try {
      // Show confirmation for Amazon TAM changes
      if (key === 'amazonTAMEnabled') {
        setPendingChange({ key, value });
        setShowConfirmDialog(true);
      } else {
        await adSettingsService.saveSettings({ [key]: value });
        // Reload settings to get the updated values
        const updatedSettings = await adSettingsService.getSettings();
        setSettings(updatedSettings);
        setReviveServerUrl(updatedSettings.reviveServerUrl);
        setReviveZones(updatedSettings.reviveZones);
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleConfirmChange = async () => {
    if (pendingChange) {
      try {
        await adSettingsService.saveSettings({ [pendingChange.key]: pendingChange.value });
        // Reload settings to get the updated values
        const updatedSettings = await adSettingsService.getSettings();
        setSettings(updatedSettings);
        setReviveServerUrl(updatedSettings.reviveServerUrl);
        setReviveZones(updatedSettings.reviveZones);
        setPendingChange(null);
      } catch (error) {
        console.error('Error updating setting:', error);
        // You might want to show an error message to the user here
      }
    }
    setShowConfirmDialog(false);
  };

  const handleCancelChange = () => {
    setPendingChange(null);
    setShowConfirmDialog(false);
  };

  const handleResetSettings = async () => {
    try {
      // Reset to default settings
      const defaultSettings = {
        amazonTAMEnabled: false,
        prebidEnabled: true,
        personalizedAdsEnabled: true,
        adRefreshEnabled: true,
        debugMode: false,
        reviveEnabled: false,
        reviveServerUrl: 'http://localhost/revive',
        reviveZones: {
          header: 1,
          sidebar: 2,
          mobile: 3,
          inContent: 4,
          footer: 5,
        },
      };
      await adSettingsService.saveSettings(defaultSettings);
      // Reload settings
      const updatedSettings = await adSettingsService.getSettings();
      setSettings(updatedSettings);
      setReviveServerUrl(updatedSettings.reviveServerUrl);
      setReviveZones(updatedSettings.reviveZones);
    } catch (error) {
      console.error('Error resetting settings:', error);
    }
  };

  const handleReviveServerUrlChange = async (url: string) => {
    setReviveServerUrl(url);
    try {
      await adSettingsService.saveSettings({ reviveServerUrl: url });
    } catch (error) {
      console.error('Error updating Revive server URL:', error);
    }
  };

  const handleReviveZoneChange = async (zone: keyof AdSettings['reviveZones'], value: number) => {
    const newZones = { ...reviveZones, [zone]: value };
    setReviveZones(newZones);
    try {
      await adSettingsService.saveSettings({ reviveZones: newZones });
    } catch (error) {
      console.error('Error updating Revive zones:', error);
    }
  };

  const testReviveConnection = async () => {
    setTestingRevive(true);
    setReviveTestResult(null);

    try {
      const config = await adSettingsService.getReviveConfig();
      await reviveAdService.updateConfig(config);
      
      // Test a basic zone
      const testResult = await reviveAdService.testZone(reviveZones.header);
      setReviveTestResult(testResult ? 'success' : 'error');
    } catch (error) {
      console.error('Revive test failed:', error);
      setReviveTestResult('error');
    } finally {
      setTestingRevive(false);
    }
  };

  const getSettingDescription = (key: keyof AdSettings): string => {
    switch (key) {
      case 'amazonTAMEnabled':
        return 'Enable Amazon Transparent Ad Marketplace for additional revenue optimization. May require page refresh.';
      case 'prebidEnabled':
        return 'Enable header bidding through Prebid.js for better ad revenue.';
      case 'personalizedAdsEnabled':
        return 'Allow personalized ads based on your interests and browsing behavior.';
      case 'adRefreshEnabled':
        return 'Automatically refresh ads after a certain time period.';
      case 'debugMode':
        return 'Show detailed ad loading information in the browser console.';
      case 'reviveEnabled':
        return 'Enable Revive Ad Server integration for serving custom fashion ads.';
      default:
        return '';
    }
  };

  const getSettingLabel = (key: keyof AdSettings): string => {
    switch (key) {
      case 'amazonTAMEnabled':
        return 'Amazon TAM';
      case 'prebidEnabled':
        return 'Header Bidding';
      case 'personalizedAdsEnabled':
        return 'Personalized Ads';
      case 'adRefreshEnabled':
        return 'Auto Refresh';
      case 'debugMode':
        return 'Debug Mode';
      case 'reviveEnabled':
        return 'Revive Ad Server';
      default:
        return key;
    }
  };

  const getSettingStatus = (key: keyof AdSettings): 'default' | 'success' | 'warning' | 'error' => {
    if (!settings) return 'default';
    
    switch (key) {
      case 'amazonTAMEnabled':
        return settings[key] ? 'success' : 'default';
      case 'debugMode':
        return settings[key] ? 'warning' : 'default';
      case 'reviveEnabled':
        return settings[key] ? 'success' : 'default';
      default:
        return settings[key] ? 'success' : 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box>
        <Card>
          <CardContent>
            <Alert severity="error">
              Failed to load ad settings. Please refresh the page.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardHeader
          avatar={<Settings />}
          title="Ad System Settings"
          subheader="Configure ad display and revenue optimization preferences"
        />
        <CardContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              These settings control how ads are displayed and optimized. Changes may require a page refresh to take effect.
            </Typography>
          </Alert>

          {/* Amazon TAM Setting */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {getSettingLabel('amazonTAMEnabled')}
                </Typography>
                <Chip
                  label={settings.amazonTAMEnabled ? 'Enabled' : 'Disabled'}
                  color={getSettingStatus('amazonTAMEnabled')}
                  size="small"
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.amazonTAMEnabled}
                    onChange={(e) => handleSettingChange('amazonTAMEnabled', e.target.checked)}
                    color="primary"
                  />
                }
                label=""
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getSettingDescription('amazonTAMEnabled')}
            </Typography>
            {settings.amazonTAMEnabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Amazon TAM is enabled. This will load additional scripts and may affect page performance.
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Revive Ad Server Setting */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {getSettingLabel('reviveEnabled')}
                </Typography>
                <Chip
                  label={settings.reviveEnabled ? 'Enabled' : 'Disabled'}
                  color={getSettingStatus('reviveEnabled')}
                  size="small"
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.reviveEnabled}
                    onChange={(e) => handleSettingChange('reviveEnabled', e.target.checked)}
                    color="primary"
                  />
                }
                label=""
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getSettingDescription('reviveEnabled')}
            </Typography>

            {/* Revive Configuration Accordion */}
            <Accordion disabled={!settings.reviveEnabled}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Storage />
                  <Typography variant="subtitle1">Revive Server Configuration</Typography>
                  {reviveTestResult === 'success' && <CheckCircle color="success" fontSize="small" />}
                  {reviveTestResult === 'error' && <ErrorIcon color="error" fontSize="small" />}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Revive Server URL"
                        value={reviveServerUrl}
                        onChange={(e) => handleReviveServerUrlChange(e.target.value)}
                        placeholder="http://localhost/revive"
                        helperText="URL to your Revive Ad Server installation"
                        disabled={!settings.reviveEnabled}
                      />
                      <Tooltip title="Test Connection">
                        <IconButton
                          onClick={testReviveConnection}
                          disabled={!settings.reviveEnabled || testingRevive}
                          color="primary"
                        >
                          <Science />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {testingRevive && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Testing connection to Revive server...
                      </Alert>
                    )}
                    {reviveTestResult === 'success' && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Successfully connected to Revive server!
                      </Alert>
                    )}
                    {reviveTestResult === 'error' && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to connect to Revive server. Please check the URL and server status.
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Zone Configuration
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(reviveZones).map(([zoneName, zoneId]) => (
                        <Grid item xs={6} sm={4} key={zoneName}>
                          <TextField
                            fullWidth
                            label={`${zoneName.charAt(0).toUpperCase() + zoneName.slice(1)} Zone`}
                            type="number"
                            value={zoneId}
                            onChange={(e) => handleReviveZoneChange(
                              zoneName as keyof AdSettings['reviveZones'], 
                              parseInt(e.target.value) || 1
                            )}
                            disabled={!settings.reviveEnabled}
                            inputProps={{ min: 1 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Other Settings */}
          {(['prebidEnabled', 'personalizedAdsEnabled', 'adRefreshEnabled', 'debugMode'] as const).map((key) => (
            <Box key={key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">
                    {getSettingLabel(key)}
                  </Typography>
                  <Chip
                    label={settings[key] ? 'Enabled' : 'Disabled'}
                    color={getSettingStatus(key)}
                    size="small"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings[key]}
                      onChange={(e) => handleSettingChange(key, e.target.checked)}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {getSettingDescription(key)}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Reset Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleResetSettings}
              color="secondary"
            >
              Reset to Defaults
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelChange}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Confirm Amazon TAM Change
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {pendingChange?.value
              ? 'Are you sure you want to enable Amazon TAM? This will:'
              : 'Are you sure you want to disable Amazon TAM? This will:'}
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                {pendingChange?.value
                  ? 'Load additional Amazon advertising scripts'
                  : 'Stop loading Amazon advertising scripts'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {pendingChange?.value
                  ? 'Potentially increase ad revenue through Amazon bidding'
                  : 'Reduce ad revenue optimization from Amazon'}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Require a page refresh to take effect
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChange} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmChange} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdSettingsComponent;
// Force update timestamp: 2025-08-01 15:01:46
